import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@chariot/web-ui';

import { loginHref } from './session';

export type LoginError = 'state' | 'exchange' | 'provider';

const ERROR_TEXT: Record<LoginError, { title: string; description: string }> = {
  state: {
    title: 'That sign-in link has expired',
    description:
      'Start again from this page. Sign-in links are single-use and expire after ten minutes.',
  },
  exchange: {
    title: 'Sign-in could not be completed',
    description:
      'Google confirmed who you are, but the session could not be created. Try again; if it persists, contact support.',
  },
  provider: {
    title: 'Google did not complete sign-in',
    description: 'You may have cancelled, or your account may not have access to Chariot.',
  },
};

export function isLoginError(value: unknown): value is LoginError {
  return value === 'state' || value === 'exchange' || value === 'provider';
}

export interface LoginScreenProps {
  /**
   * The identity provider registered on the pool — `GoogleWorkspace` when
   * deployed. Cognito reserves the name `Google` for its built-in social
   * provider, so the SAML provider from Google Workspace cannot use it.
   * Locally the pool holds native users and the auth function ignores a
   * provider it does not know, so the same button opens the hosted UI instead.
   */
  readonly provider?: string;
  readonly error?: LoginError | undefined;
  readonly returnTo?: string | undefined;
}

/**
 * The one screen outside the shell. There is no password field: identity is
 * Google's (architecture/06), so "forgot password" is Google's account recovery,
 * not something Chariot can offer or would want to hold.
 */
export function LoginScreen({ provider = 'GoogleWorkspace', error, returnTo }: LoginScreenProps) {
  return (
    <main className="bg-background text-foreground flex min-h-svh items-center justify-center p-page">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle level={2}>Sign in to Chariot</CardTitle>
          <CardDescription>
            Use your firm Google account. There is no separate password.
          </CardDescription>
        </CardHeader>
        <CardContent className="gap-field flex flex-col">
          {error === undefined ? null : (
            <Alert variant="error" live="polite" icon="warning">
              <AlertTitle>{ERROR_TEXT[error].title}</AlertTitle>
              <AlertDescription>{ERROR_TEXT[error].description}</AlertDescription>
            </Alert>
          )}
          <Button asChild variant="primary" size="lg" leftIcon="shield-check">
            <a href={loginHref({ provider, ...(returnTo === undefined ? {} : { returnTo }) })}>
              Continue with Google
            </a>
          </Button>
          <p className="text-caption text-muted-foreground">
            Trouble with your Google account? Password resets and recovery are handled by Google,
            not by Chariot.
          </p>
        </CardContent>
      </Card>
    </main>
  );
}
